import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './IveriPaymentForm.css';

/**
 * مكون نموذج الدفع iVeri
 * يعرض نموذج HTML يتم إرساله مباشرة إلى خادم iVeri
 */
const IveriPaymentForm = ({ 
    formData, 
    onSubmit, 
    autoSubmit = false,
    showInfo = true 
}) => {
    const { t } = useTranslation();
    const formRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    useEffect(() => {
        // Auto-submit إذا طُلب ذلك (بعد ثانية واحدة)
        if (autoSubmit && formData && !isSubmitting) {
            setIsSubmitting(true);
            const timer = setTimeout(() => {
                formRef.current?.submit();
            }, 1000);
            
            return () => clearTimeout(timer);
        }
    }, [autoSubmit, formData, isSubmitting]);
    
    // إذا لم تكن البيانات متوفرة بعد
    if (!formData) {
        return (
            <div className="iveri-loading">
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-3">{t('preparing_payment')}</p>
            </div>
        );
    }
    
    const handleSubmit = async (e) => {
        e.preventDefault();  // منع الإرسال الافتراضي
        setIsSubmitting(true);
        
        try {
            // استدعاء onSubmit وانتظار الـ Promise
            if (onSubmit) {
                const result = await onSubmit();
                // إذا فشل الحفظ، لا نرسل النموذج
                if (!result?.status) {
                    setIsSubmitting(false);
                    return;
                }
            }
            // بعد نجاح الحفظ، إرسال النموذج يدوياً
            formRef.current?.submit();
        } catch (error) {
            console.error('Error saving registration:', error);
            setIsSubmitting(false);
        }
    };
    
    return (
        <div className="iveri-payment-container">
            {/* رسالة تحذير للوضع التجريبي */}
            {/* {formData.sandbox && (
                <div className="alert alert-warning mb-3 d-flex align-items-center">
                    <i className="fa fa-exclamation-triangle mr-2"></i>
                    <div>
                        <strong>{t('test_mode_active')}</strong>
                        <p className="mb-0 small">{t('test_mode_description')}</p>
                    </div>
                </div>
            )} */}
            
            {/* معلومات عن iVeri */}
            {showInfo && (
                <div className="iveri-info mb-4">
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <h5 className="mb-3">
                                <i className="fa fa-shield-alt text-primary mr-2"></i>
                                {t('Pay_now_using_Debit_Credit')}
                            </h5>
                            {/* <p className="text-muted mb-3">
                                {t('iveri_description')}
                            </p> */}
                            <ul className="iveri-features list-unstyled">
                                <li className="mb-2">
                                    <i className="fa fa-check-circle text-success mr-2"></i>
                                    {t('secure_payment')}
                                </li>
                                <li className="mb-2">
                                    <i className="fa fa-credit-card text-success mr-2"></i>
                                    {t('all_cards_accepted')}
                                </li>
                                <li className="mb-2">
                                    <i className="fa fa-lock text-success mr-2"></i>
                                    {t('encrypted_transaction')}
                                </li>
                            </ul>
                            
                            {/* معلومات المبلغ */}
                            <div className="payment-amount-info mt-3 p-3 bg-light rounded">
                                <div className="row">
                                    <div className="col-6">
                                        <small className="text-muted">{t('amount')}</small>
                                        <h4 className="mb-0">
                                            $ {formData.amount?.toFixed(2)} 
                                        </h4>
                                    </div>
                                    {/* <div className="col-6 text-right">
                                        <small className="text-muted">{t('order_id')}</small>
                                        <p className="mb-0 small font-weight-bold">
                                            {formData.orderId}
                                        </p>
                                    </div> */}
                                </div>
                            </div>
                        </div>
                        
                        {/* <div className="col-md-4 text-center mt-3 mt-md-0">
                            <div className="iveri-logo-container">
                                <img 
                                    src="/assets/images/iveri-logo.png" 
                                    alt="iVeri" 
                                    className="iveri-logo img-fluid"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                                <div className="mt-2">
                                    <span className="badge badge-primary">
                                        {t('powered_by_iveri')}
                                    </span>
                                </div>
                            </div>
                        </div> */}
                    </div>
                </div>
            )}
            
            {/* رسالة التحويل */}
            {isSubmitting && (
                <div className="iveri-redirecting">
                    <div className="spinner">
                        <div className="double-bounce1"></div>
                        <div className="double-bounce2"></div>
                    </div>
                    <h5 className="mt-3">{t('redirecting_to_payment')}</h5>
                    <p className="text-white-50">{t('please_wait')}</p>
                    <small className="text-white-50">
                        {t('do_not_close_window')}
                    </small>
                </div>
            )}
            
            {/* النموذج */}
            <form 
                ref={formRef}
                action={formData.action} 
                method="post"
                onSubmit={handleSubmit}
                className={isSubmitting ? 'd-none' : ''}
                id="iveri-payment-form"
            >
                {/* الحقول المخفية */}
                {Object.entries(formData.fields).map(([key, value]) => (
                    <input 
                        key={key}
                        type="hidden" 
                        name={key} 
                        value={value} 
                    />
                ))}
                
                {/* زر الدفع */}
                {!autoSubmit && (
                    <div className="text-center mt-4">
                        <button 
                            type="submit" 
                            className="btn btn-primary btn-lg iveri-submit-btn px-5 py-3"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm mr-2"></span>
                                    {t('processing')}
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-lock mr-2"></i>
                                    {t('proceed_to_payment')}
                                    <i className="fa fa-arrow-right ml-2"></i>
                                </>
                            )}
                        </button>
                        
                        <p className="text-muted small mt-3 mb-0">
                            <i className="fa fa-info-circle mr-1"></i>
                            {t('secure_redirect_notice')}
                        </p>
                    </div>
                )}
            </form>
            
            {/* معلومات إضافية */}
            {!isSubmitting && (
                <div className="iveri-footer mt-4 pt-3 border-top">
                    <div className="row text-center text-muted small">
                        <div className="col-md-4 mb-2 mb-md-0">
                            <i className="fa fa-shield-alt mr-1"></i>
                            {t('ssl_secured')}
                        </div>
                        <div className="col-md-4 mb-2 mb-md-0">
                            <i className="fa fa-user-shield mr-1"></i>
                            {t('pci_compliant')}
                        </div>
                        <div className="col-md-4">
                            <i className="fa fa-clock mr-1"></i>
                            {t('instant_processing')}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IveriPaymentForm;

